"use client";
import { Scale, Shield, FileText, Building2, Lock } from "lucide-react";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";
import IconBox from "@/components/Icons/iconbox";

export default function LegalNoticePage() {
  const sections = [
    {
      id: "about",
      icon: Building2,
      title: "About Luminar Consult",
      content: `By accessing this website, you agree to the following terms and conditions.

Luminar Consult Limited is a management consultancy specialising in programme and project management (PPM) advisory services. We are performance leaders in programme delivery — partnering with clients to deliver and assure complex business transformation through disciplined execution and strategic insight.`,
    },
    {
      id: "intellectual-property",
      icon: FileText,
      title: "Intellectual Property",
      content: `All content on this website is the property of Luminar Consult Limited. Reproduction, distribution, or transmission of any part of this site is prohibited except for individual, non-commercial use.

You may not copy, modify, or incorporate any material into other works or publications, in any form or medium, without prior written consent from Luminar Consult Limited.`,
    },
    {
      id: "disclaimer",
      icon: Scale,
      title: "Disclaimer",
      content: `The information contained on this site is provided for general guidance only. It should not be relied upon as a substitute for professional consultation. Before making business or operational decisions, please seek advice from a Luminar Consult professional.

While every effort has been made to ensure the accuracy and reliability of the information presented, Luminar Consult Limited accepts no responsibility for any errors, omissions, or outcomes resulting from its use.`,
    },
    {
      id: "external-links",
      icon: Shield,
      title: "External Links",
      content: `This site may contain links to external websites maintained by third parties over which Luminar Consult has no control. The inclusion of such links does not imply endorsement or responsibility for the content of those external sites.`,
    },
    {
      id: "security",
      icon: Lock,
      title: "Security Policy",
      content: `Luminar Consult Limited maintains industry-standard security practices to protect information provided by visitors against misuse, loss, or corruption. Access to personally identifiable data is restricted to authorised personnel who are required to preserve its confidentiality.

Please note that our website may contain links to third-party sites not covered by this or any other Luminar privacy statement.`,
    },
  ];

  return (
    <main className="min-h-screen text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.about} />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6">
        <div className="border border-standard pt-40 p-8 sm:p-12 transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] opacity-100 translate-y-0">
          <h1 className="h1 mb-6">
            Legal <span className="text-text-secondary">Notice</span>
          </h1>
          <p className="body-lg">
            Important information regarding the use of this website and our services.
          </p>
        </div>
      </section>

      {/* LEGAL SECTIONS */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 mb-24">
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div
              key={section.id}
              id={section.id}
              className="border border-standard backdrop-blur-sm p-6 sm:p-8 scroll-mt-24"
            >
              <div className="flex items-start gap-4 sm:gap-6 mb-2">
                <IconBox icon={section.icon} index={index} size="sm" />
                <h2 className="h3">{section.title}</h2>
              </div>
              <div className="ml-0 sm:ml-16">
                {section.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="body-md leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          ))}

          {/* COMPANY INFORMATION */}
          <div
            id="company-info"
            className="border border-standard backdrop-blur-sm p-6 sm:p-8"
          >
            <div className="flex items-start gap-4 sm:gap-6 mb-2">
              <IconBox icon={Building2} index={5} size="md" />
              <h2 className="h3">Company Information</h2>
            </div>
            <div className="ml-0 sm:ml-16">
              <div className="space-y-3">
                <div>
                  <p className="body-md font-bold text-text-primary">
                    LUMINAR CONSULT LIMITED
                  </p>
                  <p className="body-md mt-1">
                    Luminar Consult Limited is also referred to throughout the site as Luminar and Luminar Consult.
                  </p>
                </div>
                
                <div className="pt-4 space-y-2">
                  <p className="body-md">
                    <span className="font-semibold text-text-primary">VAT Registration No:</span> 471596560
                  </p>
                  <p className="body-md">
                    <span className="font-semibold text-text-primary">Registered in England & Wales:</span> 15785074
                  </p>
                  <p className="body-md">
                    <span className="font-semibold text-text-primary">Registered Address:</span> C/O Accrue Accounting, Unit E4 Arena Business Centre, Holyrood Close, Poole, United Kingdom, BH17 7FP
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}